import { type FC, useEffect, useState, useSyncExternalStore } from "react";
import {
  type ModalEntry,
  type RevokeCertificateProps,
  type DeleteConfirmProps,
  modalManager,
  subscribe,
  getSnapshot,
} from "./modal_store";
import { GroupDetailModal } from "../components/group_detail_modal";
import { PolicyDetailModal } from "../components/policy_detail_modal";
import { CertificateDetailModal } from "../components/certificate_detail_modal";
import { RevokeCertificateModal } from "../components/revoke_certificate_modal";
import { BulkRevokeModal } from "../components/bulk_revoke_modal";
import { RevokeAllModal } from "../components/device_groups/revoke_all_modal";
import { CreateGroupModal } from "../components/device_groups/create_group_modal";
import { EditPolicyModal } from "../components/device_groups/edit_policy_modal";
import { CreateNotificationModal } from "../components/notifications/create_notification_modal";
import { CreatePolicyModal } from "../components/create_policy_modal";

// ─── Renderer Component ──────────────────────────────────────────────────────

export const ModalManagerRenderer: FC = () => {
  const entries = useSyncExternalStore(subscribe, getSnapshot);

  useEffect(() => {
    if (entries.length === 0) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      modalManager.close();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [entries.length]);

  if (entries.length === 0) return null;

  return (
    <>
      {entries.map((entry, index) => (
        <div
          key={entry.id}
          className="vmx-modal-overlay"
          style={{ zIndex: 1000 + index }}
          onClick={() => modalManager.close(entry.id)}
        >
          <div onClick={(e) => e.stopPropagation()}>
            <ModalContent entry={entry.modal} id={entry.id} />
          </div>
        </div>
      ))}
    </>
  );
};

// ─── Modal Content Resolver ──────────────────────────────────────────────────

const ModalContent: FC<{ entry: ModalEntry; id: number }> = ({ entry, id }) => {
  const close = () => modalManager.close(id);

  switch (entry.type) {
    case "groupDetail":
      return <GroupDetailModal {...entry.props} onClose={close} />;
    case "policyDetail":
      return <PolicyDetailModal {...entry.props} onClose={close} />;
    case "certificateDetail":
      return <CertificateDetailModal {...entry.props} onClose={close} />;
    case "revokeCertificate":
      return <RevokeCertificateWrapper {...entry.props} onClose={close} />;
    case "bulkRevoke":
      return <BulkRevokeModal {...entry.props} onClose={close} />;
    case "revokeAll":
      return <RevokeAllModal {...entry.props} onClose={close} />;
    case "createGroup":
      return <CreateGroupModal {...entry.props} onClose={close} />;
    case "editPolicy":
      return <EditPolicyModal {...entry.props} onClose={close} />;
    case "createNotification":
      return <CreateNotificationModal onClose={close} />;
    case "createPolicy":
      return <CreatePolicyModal {...entry.props} onClose={close} />;
    case "deleteConfirm":
      return <DeleteConfirmModal {...entry.props} onClose={close} />;
  }
};

// ─── Wrapper: RevokeCertificateModal ─────────────────────────────────────────

const RevokeCertificateWrapper: FC<
  RevokeCertificateProps & { onClose: () => void }
> = ({ certificate, onConfirm, onClose }) => {
  const [isRevoking, setIsRevoking] = useState(false);
  const [error, setError] = useState<string | undefined>();

  return (
    <RevokeCertificateModal
      certificate={certificate}
      isRevoking={isRevoking}
      error={error}
      onClose={onClose}
      onConfirm={async () => {
        setIsRevoking(true);
        setError(undefined);
        try {
          await onConfirm();
          modalManager.closeAll();
        } catch (err) {
          setError(
            err instanceof Error ? err.message : "Failed to revoke certificate."
          );
        } finally {
          setIsRevoking(false);
        }
      }}
    />
  );
};

// ─── Inline: DeleteConfirmModal ──────────────────────────────────────────────

const DeleteConfirmModal: FC<DeleteConfirmProps & { onClose: () => void }> = ({
  title,
  message,
  onConfirm,
  confirmLabel = "Delete",
  confirmingLabel = "Deleting...",
  onClose,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | undefined>();

  return (
    <div className="vmx-modal" style={{ maxWidth: 420 }}>
      <div className="vmx-modal-header">
        <div className="vmx-modal-title">{title}</div>
      </div>
      <div className="vmx-modal-body">
        <div style={{ color: "var(--vmx-gray-600)", lineHeight: 1.6 }}>
          {message}
        </div>
        {error !== undefined && (
          <div
            style={{
              marginTop: 8,
              fontSize: "0.8125rem",
              color: "var(--vmx-danger)",
            }}
          >
            {error}
          </div>
        )}
      </div>
      <div className="vmx-modal-footer">
        <button
          className="vmx-btn vmx-btn-secondary"
          onClick={onClose}
          disabled={isProcessing}
        >
          Cancel
        </button>
        <button
          className="vmx-btn vmx-btn-danger"
          onClick={async () => {
            setIsProcessing(true);
            setError(undefined);
            try {
              await onConfirm();
              onClose();
            } catch (err) {
              setError(
                err instanceof Error ? err.message : "Operation failed."
              );
            } finally {
              setIsProcessing(false);
            }
          }}
          disabled={isProcessing}
        >
          {isProcessing ? confirmingLabel : confirmLabel}
        </button>
      </div>
    </div>
  );
};
